<script setup>
import { ref, reactive, computed } from "vue";
import {
  queryCameraAccountById,
  insertCameraAccount,
  updateCameraAccount,
} from "@/api/cameraConfig";

import { queryOrgTree } from "@/api/common";
import { ElMessage } from "element-plus";

const emits = defineEmits(["afterSave"]);

// 弹窗类型
const dialogType = ref("add");

// 弹窗标题
const title = computed({
  get() {
    return dialogType.value == "add" ? "新增" : "编辑";
  },
});

// 弹窗控制
const visible = ref(false);

// 遮罩
const loading = ref(true);

// 表单ref
const ruleRef = ref(null);

// 组织数组
const orgList = ref([]);

// 新增编辑api
const adjustApi = reactive({
  add: insertCameraAccount,
  edit: updateCameraAccount,
});

// 保存按钮防抖
const buttonLoading = ref(false);

// 树配置
const defaultProp = reactive({
  label: "organizationName",
  disabled: (data) => {
    return !["供水厂", "净水厂"].includes(data.organizationTypeStr);
  },
});

// 表单数据
const formData = reactive({
  id: null,
  host: null,
  title: null,
  org: null,
  plantsIds: null,
  appKey: null,
  appSecret: null,
  remark: null,
});

// 表单校验
const rules = reactive({
  host: [
    { required: true, message: "请输入主机地址", trigger: "blur" },
    {
      pattern: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}:[0-9]{1,5}$/,
      message: "请输入合法的主机地址",
      trigger: "blur",
    },
  ],
  title: [{ required: true, message: "请输入标题", trigger: "blur" }],
  org: [{ required: true, message: "请选择组织", trigger: "blur,change" }],
  appKey: [
    { required: true, message: "请输入appKey", trigger: "blur" },
    {
      pattern: /^[a-zA-Z0-9]+$/,
      message: "只能输入大小写英文和数字",
      trigger: "blur",
    },
  ],
  appSecret: [
    { required: true, message: "请输入appSecret", trigger: "blur" },
    {
      pattern: /^[a-zA-Z0-9]+$/,
      message: "只能输入大小写英文和数字",
      trigger: "blur",
    },
  ],
});

// 点击保存
const formSubmit = () => {
  buttonLoading.value = true;
  ruleRef.value?.validate((valid) => {
    if (valid) {
      adjustApi[dialogType.value](formData)
        .then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            visible.value = false;
            emits("afterSave");
          }
        })
        .finally(() => {
          setTimeout(() => {
            buttonLoading.value = false;
          }, 300);
        });
    } else {
      buttonLoading.value = false;
    }
  });
};

// 通过id获取账号详情
const getAccountInfo = (id) => {
  loading.value = true;
  queryCameraAccountById(id)
    .then((res) => {
      if (JSON.stringify(res.data) !== "{}") {
        for (const key in formData) {
          formData[key] = res.data[key];
        }
        formData.org = res.data.plantsIds ? res.data.plantsIds?.split(",") : [];
      }
    })
    .finally(() => {
      loading.value = false;
      console.log(formData);
    });
};

// 获取组织数
const getOrgTree = () => {
  // 不清空数组的话  多次点击弹窗会导致列表种数据变多
  orgList.value = [];
  queryOrgTree({}).then((res) => {
    extractData(res.data);
  });
};

// 将树结构转化为一维数组
const extractData = (nodes) => {
  for (let node of nodes) {
    if (["供水厂", "净水厂"].includes(node.organizationTypeStr)) {
      orgList.value.push(node);
    }

    if (node.children && node.children.length > 0) {
      extractData(node.children);
    }
  }
};

// 组织变化
const organizationChange = (val) => {
  formData.plantsIds = val.join(",");
};

// 弹窗打开
const handleDialogOpen = (row, type) => {
  dialogType.value = type;
  visible.value = true;
  // 获取组织机构树
  getOrgTree();
  // 清空表单
  ruleRef.value?.resetFields();
  formData.plantsIds = null;
  loading.value = false;
  if (row) {
    // 如果row有值 === 编辑
    // 获取详情
    getAccountInfo(row.id);
  }
};

// 提供函数给父组件
defineExpose({
  handleDialogOpen,
});
</script>

<template>
  <el-dialog
    :title="title"
    v-model="visible"
    :close-on-click-modal="false"
    width="40%"
  >
    <el-form
      ref="ruleRef"
      :model="formData"
      :rules="rules"
      v-loading="loading"
      label-width="100px"
    >
      <el-form-item label="主机地址" prop="host">
        <el-input v-model="formData.host" placeholder="请输入主机地址" />
      </el-form-item>
      <el-form-item label="标题" prop="title">
        <el-input v-model="formData.title" placeholder="请输入标题" />
      </el-form-item>
      <el-form-item label="组织" prop="org">
        <el-select
          v-model="formData.org"
          multiple
          placeholder="请选择组织"
          show-checkbox
          style="width: 100%"
          @change="organizationChange"
        >
          <el-option
            v-for="item in orgList"
            :key="item.organizationCode"
            :label="item.organizationName"
            :value="item.organizationCode"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="appKey" prop="appKey">
        <el-input v-model="formData.appKey" placeholder="请输入appKey" />
      </el-form-item>
      <el-form-item label="appSecret" prop="appSecret">
        <el-input v-model="formData.appSecret" placeholder="请输入appSecret" />
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input
          v-model="formData.remark"
          type="textarea"
          :rows="2"
          show-word-limit
          maxlength="200"
          placeholder="请输入备注"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button
          type="primary"
          @click="formSubmit()"
          :disabled="buttonLoading"
        >
          保存
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped></style>
